import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, withCtx as _withCtx, createVNode as _createVNode } from "vue"

const _hoisted_1 = { key: 1 }
const _hoisted_2 = { columns: "3" }
const _hoisted_3 = ["value", "options", "placeholder", "disabled"]
const _hoisted_4 = ["value", "placeholder", "disabled"]
const _hoisted_5 = ["value", "placeholder"]
const _hoisted_6 = ["value", "placeholder", "onTextInputChange"]
const _hoisted_7 = ["onClick"]
const _hoisted_8 = { key: 0 }
const _hoisted_9 = { key: 1 }
const _hoisted_10 = { class: "env-var-section" }
const _hoisted_11 = { class: "env-var-title-row" }
const _hoisted_12 = { class: "env-var-data" }
const _hoisted_13 = ["value", "onTextInputChange"]
const _hoisted_14 = ["value", "onCodeInputChange"]
const _hoisted_15 = ["onClick"]
const _hoisted_16 = { class: "config-data-actions" }

import { ref, watch, computed } from 'vue';
import { useStore } from 'vuex';
import Loading from '@shell/components/Loading.vue';
import Banner from '@components/Banner/Banner.vue';
import ChartValues from '../settings/ChartValues.vue';
import { _EDIT } from '@shell/config/query-params';

import { sortBy } from '@shell/utils/sort';
import { validateKubernetesName } from '@shell/utils/validators/kubernetes-name';
import { EPINIO_TYPES, EpinioNamespace, EpinioAppInfo } from '../../types';
import Application from '../../models/applications';
import { objValuesToString } from '../../utils/settings';


export default /*@__PURE__*/_defineComponent({
  __name: 'AppInfo',
  props: {
    application: {},
    mode: {},
    source: {},
    active: { type: Boolean }
  },
  emits: ["valid", "change"],
  setup(__props: any, { emit: __emit }) {

const store = useStore();

const t = store.getters['i18n/t'];

// Props
const props = __props;

// Emit function
const emit = __emit;

// Reactive state
const errors = ref<string[]>([]); // eslint-disable-line @typescript-eslint/no-unused-vars
const values = ref<EpinioAppInfo | undefined>(undefined);
const validSettings = ref<{ [key: string]: boolean }>({});
const envVariables = ref<{ key: string; value: string }[]>([]);
const bulkFileInput = ref<HTMLInputElement | null>(null);
const fileDialogActive = ref(false);

// Computed properties
const namespaces = computed(() => {
  void store.state.activeNamespaceCacheKey;
  const active = store.state.activeNamespaceCache;
  const activeNamespaces = store.getters['epinio/all'](EPINIO_TYPES.NAMESPACE).filter((ns: EpinioNamespace) => {
    if (!active || Object.keys(active).length === 0) return true;
    const isActive = active[ns.metadata.name];
    return isActive;
  });
  if (activeNamespaces.length === 1) {
    handleNameNsUpdate({ metadata: { namespace: activeNamespaces[0].metadata.name } });
  }
  return sortBy(activeNamespaces, 'name', false);
});

const namespaceNames = computed(() => namespaces.value.map((n: EpinioNamespace) => ({
  label: n.metadata.name,
  value: n.metadata.name
})));

const valid = computed(() => {
  if (!values.value) {
    return false;
  }
  const validName = !!values.value.meta?.name;

  // Namespace must be selected (not empty) and pass naming validation
  const namespaceValue = values.value.meta?.namespace || '';
  const hasNamespace = !!namespaceValue;
  const nsErrors = validateKubernetesName(
    namespaceValue,
    '',
    store.getters,
    undefined,
    [],
  );
  const validNamespace = hasNamespace && nsErrors.length === 0;
  const validInstances = typeof Number(values.value.configuration?.instances) !== 'string' &&
    values.value.configuration?.instances >= 0;

  return validName && validNamespace && validInstances &&
    Object.values(validSettings.value).every((v) => !!v);
});

const showApplicationVariables = computed(() => {
  return Object.keys(values.value?.configuration?.settings || {}).length !== 0;
});

const isEdit = computed(() => props.mode === _EDIT);

// Generate a default name for new applications
const generateDefaultName = () => {
  try {
    // Use source prop if available (create mode), otherwise try appSource (edit mode)
    const source = props.source || props.application.appSource;
    if (!source) {
      return '';
    }

    let baseName = '';

    // Determine base name from source
    if (source.git?.repo?.name) {
      baseName = source.git.repo.name;
    } else if (source.gitUrl?.url) {
      // Extract base name from git URL
      const urlParts = source.gitUrl.url.split('/');
      baseName = urlParts[urlParts.length - 1].replace(/\.git$/, '');
    } else if (source.container?.url) {
      // Extract base name from container URL
      const urlParts = source.container.url.split('/');
      const imageWithTag = urlParts[urlParts.length - 1];
      baseName = imageWithTag.split(':')[0];
    }
    // Append random string to the end of the base name
    if (baseName) {
      const randomSuffix = Math.random().toString(36).substring(2, 8);
      return `${baseName}-${randomSuffix}`.toLowerCase();
    }

    return '';
  } catch (e) {
    console.log(e);
    return '';
  }
};

watch(() => props.active, (isActive) => {
  if (isActive) {
    const defaultName = props.application.meta?.name || (props.mode !== _EDIT ? generateDefaultName() : '');

    // In create mode, don't auto-select the first namespace - require explicit selection
    const defaultNamespace = props.mode === _EDIT
      ? (props.application.meta?.namespace || '')
      : (props.application.meta?.namespace || '');

    const valuesData: EpinioAppInfo = {
      meta: {
        name: defaultName,
        namespace: defaultNamespace
      },
      chart: moveBooleansToFront(props.application.chart?.settings) || {},
      configuration: {
        configurations: props.application.configuration?.configurations || [],
        instances: props.application.configuration?.instances ?? 1,
        environment: props.application.configuration?.environment || {},
        settings: props.application.configuration?.settings || {},
        routes: props.application.configuration?.routes || [],
      },
    };

    envVariables.value = Object.entries(valuesData.configuration.environment).map(([key, value]) => ({ key, value }));
    values.value = valuesData;
    validSettings.value = {};

    emit('valid', valid.value);

    populateOnEdit();
  }
})

// Methods
const update = () => {
  emit('change', {
    meta: values.value?.meta,
    configuration: {
      ...values.value?.configuration,
      settings: objValuesToString(values.value?.configuration.settings)
    },
  });
};

// Watchers
watch(() => values.value?.configuration.instances, (newVal) => {
  values.value.configuration.instances = Number(newVal);
  update()
});
watch(() => values.value?.configuration.environment, update);
watch(() => values.value?.configuration.settings, update, { deep: true });
watch(() => values.value?.configuration.routes, update);
watch(valid, (newValid) => {
  emit('valid', newValid);
});
watch(envVariables, (newEnvVars) => {
  values.value.configuration.environment = newEnvVars.reduce((acc, { key, value }) => {
    if (key && value) {
      acc[key] = value;
      return acc;
    } else {
      return acc;
    }
  }, {});
  update();
}, { deep: true });

// Handler for name and namespace updates
function handleNameNsUpdate(updatedValue: { metadata?: { name?: string; namespace?: string } }) {
  if (updatedValue?.metadata && values.value?.meta) {
    if (updatedValue.metadata.name !== undefined) {
      values.value.meta.name = updatedValue.metadata.name;
    }
    if (updatedValue.metadata.namespace !== undefined) {
      values.value.meta.namespace = updatedValue.metadata.namespace;
    }
  }
  update();
}

const populateOnEdit = async () => {
  // We need to fetch the chart settings on edit mode.
  if (isEdit.value || props.mode === 'view') {
    const chartList = await store.dispatch(
      'epinio/findAll',
      { type: EPINIO_TYPES.APP_CHARTS },
    );

    const filterChart = chartList?.find(
      (chart: any) => chart.id === props.application.configuration.appchart
    );

    if (filterChart?.settings) {
      const customValues = Object.keys(filterChart?.settings).reduce((acc: any, key: any) => {
        acc[key] = props.application.configuration.settings[key] || '';
        return acc;
      }, {});

      values.value.configuration.settings = customValues;
      values.value.chart = moveBooleansToFront(filterChart.settings);
    }
  }
};

// Allows us to move the checkbox at the top of the list so layout-wise looks better
const moveBooleansToFront = (settingsObj: any) => {
  if (!settingsObj) {
    return;
  }
  const entries = Object.entries(settingsObj);

  entries.sort((a: any, b: any) => {
    const aValue = a[1].type === 'bool' ? 0 : 1;
    const bValue = b[1].type === 'bool' ? 0 : 1;

    return aValue - bValue;
  });

  return Object.fromEntries(entries);
};

const addRow = () => {
  envVariables.value.push({ key: '', value: '' });
};

const removeRow = (index: number) => {
  envVariables.value.splice(index, 1);
};

const updateRowKey = (index: number, newKey: string) => {
  envVariables.value[index].key = newKey;
};

const updateRowValue = (index: number, newValue: string) => {
  envVariables.value[index].value = newValue;
};

// "Add from file", parse a KEY=VALUE file and add rows
function triggerBulkFileUpload() {
  fileDialogActive.value = true;
  bulkFileInput.value?.click();
}

// Parse a simple KEY=VALUE file, ignoring empty lines and comments (lines starting with #)
function onBulkFileChange(event: Event) {
  fileDialogActive.value = false;
  const file = (event.target as HTMLInputElement).files?.[0];

  if (!file) return;

  const reader = new FileReader();

  reader.onload = (e) => {
    const text = (e.target?.result as string) || '';
    const newRows: Array<{ key: string; value: string }> = [];

    text.split('\n').forEach((line) => {
      const trimmed = line.trim();

      if (!trimmed || trimmed.startsWith('#')) return;

      const sep = trimmed.indexOf('=');

      if (sep > 0) {
        newRows.push({ key: trimmed.slice(0, sep).trim(), value: trimmed.slice(sep + 1) });
      }
    });

    // If there are new rows, add them to the existing config data. If the existing data is just one empty row, replace it instead.
    if (newRows.length) {
      const existing = envVariables.value;
      const onlyEmptyRow = existing.length === 1 && !existing[0].key && !existing[0].value;

      envVariables.value = onlyEmptyRow ? newRows : [...existing, ...newRows];
    }
  };
  reader.readAsText(file);
  (event.target as HTMLInputElement).value = '';
}

return (_ctx: any,_cache: any) => {
  return (!values.value)
    ? (_openBlock(), _createBlock(Loading, { key: 0 }))
    : (_openBlock(), _createElementBlock("trailhand-form-card", _hoisted_1, [
        _createElementVNode("trailhand-form-row", _hoisted_2, [
          _createElementVNode("trailhand-dropdown", {
            value: values.value.meta.namespace,
            options: namespaceNames.value,
            label: "Namespace",
            placeholder: _unref(t)('epinio.applications.create.namespacePlaceholder'),
            disabled: isEdit.value,
            "data-testid": "epinio_app-info_namespace",
            required: "",
            onDropdownChange: _cache[0] || (_cache[0] = (e) => handleNameNsUpdate({ metadata: { namespace: e.detail.value } }))
          }, null, 40, _hoisted_3),
          _cache[7] || (_cache[7] = _createTextVNode()),
          _createElementVNode("trailhand-text-input", {
            value: values.value.meta.name,
            "data-testid": "epinio_app-info_name",
            label: "Name",
            placeholder: _unref(t)('epinio.applications.create.namePlaceholder'),
            disabled: isEdit.value,
            required: "",
            onTextInputChange: _cache[1] || (_cache[1] = (e) => handleNameNsUpdate({ metadata: { name: e.detail.value } }))
          }, null, 40, _hoisted_4),
          _cache[8] || (_cache[8] = _createTextVNode()),
          _createElementVNode("trailhand-text-input", {
            value: values.value.configuration.instances,
            "data-testid": "epinio_app-info_instances",
            label: "Instances",
            placeholder: _unref(t)('epinio.applications.create.instancesPlaceholder'),
            required: "",
            type: "number",
            min: "0",
            onTextInputChange: _cache[2] || (_cache[2] = (e) => {values.value.configuration.instances = e.detail.value; update()})
          }, null, 40, _hoisted_5)
        ]),
        _cache[19] || (_cache[19] = _createTextVNode()),
        _createElementVNode("div", null, [
          _cache[10] || (_cache[10] = _createElementVNode("h3", null, "Routes", -1)),
          _cache[11] || (_cache[11] = _createTextVNode()),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(values.value.configuration.routes, (route, index) => {
            return (_openBlock(), _createElementBlock("div", {
              key: index,
              class: "route-item"
            }, [
              _createElementVNode("trailhand-text-input", {
                style: {"flex":"1"},
                value: route,
                placeholder: _unref(t)('epinio.applications.create.routes.placeholder'),
                onTextInputChange: (e) => { values.value.configuration.routes[index] = e.detail.value; update(); }
              }, null, 40, _hoisted_6),
              _cache[9] || (_cache[9] = _createTextVNode()),
              (props.mode !== 'view')
                ? (_openBlock(), _createElementBlock("button", {
                    key: 0,
                    class: "remove-link",
                    onClick: () => { values.value.configuration.routes.splice(index, 1); update(); }
                  }, "\n          Remove\n        ", 8, _hoisted_7))
                : _createCommentVNode("", true)
            ]))
          }), 128)),
          _cache[12] || (_cache[12] = _createTextVNode()),
          (props.mode !== 'view')
            ? (_openBlock(), _createElementBlock("trailhand-button", {
                key: 0,
                variant: "alternate",
                onButtonClick: _cache[3] || (_cache[3] = () => { values.value.configuration.routes.push(''); update(); })
              }, "\n        Add Row\n      ", 32))
            : _createCommentVNode("", true)
        ]),
        _cache[20] || (_cache[20] = _createTextVNode()),
        (isEdit.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
              _createVNode(Banner, { color: "info" }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_unref(t)('epinio.applications.create.settingsVars.description')), 1)
                ]),
                _: 1
              })
            ]))
          : _createCommentVNode("", true),
        _cache[21] || (_cache[21] = _createTextVNode()),
        (showApplicationVariables.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
              _createVNode(ChartValues, {
                value: values.value.configuration.settings,
                "onUpdate:value": _cache[4] || (_cache[4] = ($event: any) => ((values.value.configuration.settings) = $event)),
                chart: values.value.chart,
                title: _unref(t)('epinio.applications.create.settingsVars.title'),
                mode: props.mode,
                disabled: false,
                onValid: _cache[5] || (_cache[5] = ($event: any) => (validSettings.value = $event))
              }, null, 8, ["value", "chart", "title", "mode"])
            ]))
          : _createCommentVNode("", true),
        _cache[22] || (_cache[22] = _createTextVNode()),
        _createElementVNode("div", _hoisted_10, [
          _createElementVNode("div", _hoisted_11, [
            _createElementVNode("h3", null, _toDisplayString(_unref(t)('epinio.applications.create.envvar.title')), 1)
          ]),
          _cache[18] || (_cache[18] = _createTextVNode()),
          _createElementVNode("div", _hoisted_12, [
            (envVariables.value.length > 0 || isEdit.value)
              ? (_openBlock(true), _createElementBlock(_Fragment, { key: 0 }, _renderList(envVariables.value, (envVar, i) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: i,
                    class: "env-var-row"
                  }, [
                    _createElementVNode("trailhand-text-input", {
                      style: {"flex":"1"},
                      value: envVar.key,
                      label: "Key",
                      required: "",
                      placeholder: "e.g. foo",
                      onTextInputChange: (e) => updateRowKey(i, e.detail.value)
                    }, null, 40, _hoisted_13),
                    _cache[13] || (_cache[13] = _createTextVNode()),
                    _createElementVNode("trailhand-code-editor", {
                      style: {"flex":"1"},
                      value: envVar.value,
                      label: "Value",
                      required: "",
                      onCodeInputChange: (e) => updateRowValue(i, e.detail.value)
                    }, null, 40, _hoisted_14),
                    _cache[14] || (_cache[14] = _createTextVNode()),
                    _createElementVNode("button", {
                      class: "remove-link",
                      onClick: ($event: any) => (removeRow(i))
                    }, "\n              Remove\n            ", 8, _hoisted_15)
                  ]))
                }), 128))
              : _createCommentVNode("", true),
            _cache[17] || (_cache[17] = _createTextVNode()),
            _createElementVNode("div", _hoisted_16, [
              _createElementVNode("trailhand-button", {
                variant: "alternate",
                onButtonClick: addRow
              }, "\n            Add\n          ", 32),
              _cache[15] || (_cache[15] = _createTextVNode()),
              _createElementVNode("trailhand-button", {
                variant: "alternate",
                onButtonClick: triggerBulkFileUpload
              }, "\n            Read From File\n          ", 32),
              _cache[16] || (_cache[16] = _createTextVNode()),
              _createElementVNode("input", {
                ref_key: "bulkFileInput",
                ref: bulkFileInput,
                type: "file",
                class: "hidden-file-input",
                onChange: onBulkFileChange,
                onCancel: _cache[6] || (_cache[6] = ($event: any) => (fileDialogActive.value = false))
              }, null, 544)
            ])
          ])
        ])
      ]))
}
}

})